import React, { useState, useEffect } from 'react';
import api from '../services/api';

const SectorsManagement = ({ user }) => {
  const [sectors, setSectors] = useState([]);
  const [clients, setClients] = useState([]);
  const [devices, setDevices] = useState([]);
  const [sites, setSites] = useState([]); // para mapear site->client
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const emptyForm = { name: '', description: '', client: '', devices: [] };
  const [form, setForm] = useState(emptyForm);

  const loadAll = async () => {
    try {
      const [s, c, d, st] = await Promise.all([
        api.get('/api/sectors'),
        api.get('/api/clients'),
        api.get('/api/devices'),
        api.get('/api/sites')
      ]);
      setSectors(s.data || []);
      setClients(c.data || []);
      setDevices(d.data || []);
      setSites(st.data || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  // Map siteId -> clientId, derivado de la lista de sitios
  const siteClientMap = {};
  sites.forEach(s => { siteClientMap[String(s._id)] = s.client ? String(s.client) : null; });

  useEffect(() => { loadAll(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = async (sector) => {
    try {
      const r = await api.get(`/api/sectors/${sector._id}`);
      const full = r.data;
      setEditing(full);
      setForm({
        name: full.name || '',
        description: full.description || '',
        client: full.client?._id || full.client || '',
        devices: (full.devices || []).map(d => d._id || d)
      });
      setShowForm(true);
    } catch (e) {
      alert('Error al cargar sector: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return alert('El nombre es obligatorio');
    if (!form.client) return alert('Seleccionar el cliente');
    try {
      if (editing) {
        await api.put(`/api/sectors/${editing._id}`, form);
      } else {
        await api.post('/api/sectors', form);
      }
      setShowForm(false);
      loadAll();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.message || e.message));
    }
  };

  const handleDelete = async (sector) => {
    if (!window.confirm(`¿Eliminar sector "${sector.name}"?`)) return;
    try {
      await api.delete(`/api/sectors/${sector._id}`);
      loadAll();
    } catch (e) {
      alert('Error: ' + (e.response?.data?.message || e.message));
    }
  };

  const toggleDevice = (id) => {
    setForm(f => ({
      ...f,
      devices: f.devices.includes(id) ? f.devices.filter(x => x !== id) : [...f.devices, id]
    }));
  };

  // Dispositivos cuyo sitio pertenece al cliente seleccionado
  const devicesForSelectedClient = () => {
    if (!form.client) return [];
    return devices.filter(d => {
      const siteId = d.site?._id || d.site;
      if (!siteId) return false; // device sin sitio: no asignable
      return siteClientMap[String(siteId)] === String(form.client);
    });
  };

  // Set de IDs de devices que YA pertenecen a OTRO sector (no al que estoy editando)
  const devicesInOtherSector = new Set();
  devices.forEach(d => {
    if (d.sector && (!editing || String(d.sector) !== String(editing._id)) && !form.devices.includes(d._id)) {
      devicesInOtherSector.add(d._id);
    }
  });

  if (user?.role !== 'admin') {
    return <div style={{ padding: '1rem', color: '#ef4444' }}>Solo administradores pueden gestionar sectores.</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <div>
          <h3 style={{ margin: 0, color: '#1f2937' }}>📂 Sectores</h3>
          <p style={{ margin: '0.2rem 0 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
            Agrupar dispositivos de un cliente en sectores lógicos.
          </p>
        </div>
        <button onClick={openNew} style={{
          padding: '0.55rem 1.1rem', background: '#10b981', color: 'white', border: 'none',
          borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600
        }}>+ Nuevo sector</button>
      </div>

      {loading && <div style={{ color: '#6b7280' }}>Cargando…</div>}
      {error && <div style={{ color: '#ef4444', padding: '0.75rem', background: '#fee2e2', borderRadius: '8px' }}>{error}</div>}

      {!loading && sectors.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', background: 'white', borderRadius: '8px' }}>
          No hay sectores creados. Empezá con "+ Nuevo sector".
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '0.85rem' }}>
        {sectors.map(s => (
          <div key={s._id} style={{
            background: 'white', borderRadius: '10px', border: '1px solid #e5e7eb',
            boxShadow: '0 1px 2px rgba(0,0,0,0.04)', padding: '0.85rem 1rem'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.4rem' }}>
              <h4 style={{ margin: 0, color: '#1f2937', fontSize: '1rem' }}>{s.name}</h4>
              <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.5rem', borderRadius: '4px', background: '#dbeafe', color: '#1e40af', fontWeight: 600 }}>
                {s.deviceCount} disp.
              </span>
            </div>
            <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.5rem' }}>
              <b>Cliente:</b> {s.client?.name || '—'}
            </div>
            {s.description && <div style={{ fontSize: '0.78rem', color: '#4b5563', marginBottom: '0.5rem' }}>{s.description}</div>}
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <button onClick={() => openEdit(s)} style={btnSmallSecondary}>✏️ Editar</button>
              <button onClick={() => handleDelete(s)} style={btnSmallDanger}>🗑️ Eliminar</button>
            </div>
          </div>
        ))}
      </div>

      {showForm && (
        <div style={modalBackdrop} onClick={() => setShowForm(false)}>
          <div style={modalBox} onClick={e => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>{editing ? 'Editar sector' : 'Nuevo sector'}</h3>
            <div style={{ display: 'grid', gap: '0.75rem' }}>
              <div>
                <label style={lblStyle}>Nombre del sector *</label>
                <input type="text" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                  style={inputStyle} placeholder="Ej: Planta Norte" />
              </div>
              <div>
                <label style={lblStyle}>Cliente *</label>
                <select value={form.client} onChange={e => setForm({ ...form, client: e.target.value, devices: [] })}
                  style={inputStyle}>
                  <option value="">— Seleccionar cliente —</option>
                  {clients.map(c => <option key={c._id} value={c._id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={lblStyle}>Descripción</label>
                <input type="text" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
                  style={inputStyle} placeholder="Opcional" />
              </div>

              {form.client && (
                <div>
                  <label style={lblStyle}>Dispositivos del sector</label>
                  <div style={{
                    marginTop: '0.3rem', maxHeight: '260px', overflowY: 'auto',
                    border: '1px solid #e5e7eb', borderRadius: '6px', padding: '0.5rem'
                  }}>
                    {devicesForSelectedClient().length === 0 && (
                      <div style={{ color: '#9ca3af', fontSize: '0.85rem' }}>
                        El cliente seleccionado no tiene dispositivos en sus sitios.
                      </div>
                    )}
                    {devicesForSelectedClient().map(d => {
                      const taken = devicesInOtherSector.has(d._id);
                      return (
                        <label key={d._id} style={{
                          display: 'flex', alignItems: 'center', gap: '0.5rem',
                          padding: '0.25rem', cursor: taken ? 'not-allowed' : 'pointer',
                          opacity: taken ? 0.5 : 1
                        }}>
                          <input type="checkbox" disabled={taken}
                            checked={form.devices.includes(d._id)}
                            onChange={() => toggleDevice(d._id)} />
                          <span style={{ fontSize: '0.85rem' }}>
                            {d.name} <span style={{ color: '#9ca3af' }}>({d.deviceId})</span>
                            {taken && <span style={{ fontSize: '0.7rem', color: '#dc2626', marginLeft: '0.3rem' }}>(otro sector)</span>}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.25rem', justifyContent: 'flex-end' }}>
              <button onClick={() => setShowForm(false)} style={btnSecondary}>Cancelar</button>
              <button onClick={handleSave} style={btnPrimary}>{editing ? 'Guardar cambios' : 'Crear sector'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const lblStyle = { display: 'block', fontSize: '0.8rem', color: '#374151', fontWeight: 600, marginBottom: '0.25rem' };
const inputStyle = { width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '0.875rem' };
const modalBackdrop = {
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
};
const modalBox = { background: 'white', borderRadius: '12px', padding: '1.5rem', width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto' };
const btnPrimary = { padding: '0.55rem 1.1rem', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600 };
const btnSecondary = { padding: '0.55rem 1.1rem', background: '#f3f4f6', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' };
const btnSmallSecondary = { flex: 1, padding: '0.4rem', background: '#f3f4f6', color: '#1f2937', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem' };
const btnSmallDanger = { flex: 1, padding: '0.4rem', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: '6px', cursor: 'pointer', fontSize: '0.78rem' };

export default SectorsManagement;
